import { isNil } from 'lodash';
import moment from 'moment-timezone';
import {
    ORDER_RETURN_REASON,
    ORDER_RETURN_STATUS,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { isPart } from 'common/utils/line-item-utils';
import {
    isMotiliEngineeringDept,
    isMotiliFinanceDept,
} from '../services/AuthService';

/**
 * find histories of return.
 * @param {[object]}orderReturns
 * @return {[]}
 */
export function findHistoryReturns(orderReturns) {
    if (orderReturns) {
        return orderReturns
            .filter(re => ['CLOSED', 'CANCELLED'].includes(re.statusId))
            .sort(
                (a, b) =>
                    moment(b.created).format('X') -
                    moment(a.created).format('X')
            );
    }
    return [];
}

/**
 * check if have any line items for return
 * @param {object} order
 * @return {boolean}
 */
export function haveItemsToReturn(order) {
    const filterLineItems =
        order.orderTypeId !== 'ORDER_WITH_ORIGIN'
            ? order.lineItems?.filter(li => {
                  return (
                      !li.bundleJobLineItemId &&
                      li.productCategoryId !== 'BUNDLE'
                  );
              })
            : order.lineItems;

    return !!filterLineItems?.find(li => {
        if (
            li.completedByDocumentId === order.id &&
            li.quantity - li.returnQuantity > 0
        ) {
            return _allowedLineItemsByCategories(li);
        }
        return false;
    });
}

/**
 * Find active return
 * @param {object} orderReturns
 * @return {null|{object}}
 */
export function findActiveReturn(orderReturns) {
    if (orderReturns) {
        return orderReturns.find(re =>
            [
                'REQUESTED',
                'ACTIVE',
                'COMPLETED',
                'DENIED',
                'PARTIAL_APPROVED',
                'APPROVED',
            ].includes(re.statusId)
        );
    }
    return null;
}

/**
 * Get line items for returning
 * @param {object} order
 * @param {boolean} includeBundle
 * @param {boolean} withOrigin
 * @return {[]}
 */
export function getLineItemsForActiveReturn(order, includeBundle, withOrigin) {
    return order.lineItems.filter(
        li =>
            ((includeBundle &&
                withOrigin &&
                li.productCategoryId === 'BUNDLE') ||
                (li.completedByDocumentId === order.id &&
                    _allowedLineItemsByCategories(li) &&
                    [
                        'COMPLETED',
                        'RETURNING',
                        'RETURNED',
                        'PARTIALLY_RETURNED',
                    ].includes(li.statusId) &&
                    li.returnQuantity < li.quantity)) &&
            (withOrigin || !li.bundleJobLineItemId)
    );
}

/**
 * Get line items for returning
 * @param {object} order
 * @param {boolean} includeBundle
 * @param {boolean} withOrigin
 * @return {[]}
 */
export function getLineItemsForClosedReturn(order, includeBundle, withOrigin) {
    return order.lineItems.filter(
        li =>
            ((includeBundle &&
                withOrigin &&
                li.productCategoryId === 'BUNDLE') ||
                (li.completedByDocumentId === order.id &&
                    _allowedLineItemsByCategories(li) &&
                    [
                        'COMPLETED',
                        'RETURNING',
                        'RETURNED',
                        'PARTIALLY_RETURNED',
                    ].includes(li.statusId))) &&
            (withOrigin || !li.bundleJobLineItemId)
    );
}

/**
 * Validate Purchase Adjustment Number
 * @param {string} poAdjNo
 * @return {boolean}
 */
export function validatePurchaseAdjustmentNumber(poAdjNo) {
    return /^P[EICR]ADJ\d{5,}$/.test(poAdjNo);
}

/**
 * Validate Credit Memo Number
 * @param {string} soAdjNo
 * @return {boolean}
 */
export function validateCreditMemoNumber(soAdjNo) {
    return /^[ESCR]ADJ\d{5,}$/.test(soAdjNo);
}

/**
 * Check allowed line items for returning by category and subcategory
 *
 * @param {object} lineItem
 * @return {boolean}
 * @private
 */
function _allowedLineItemsByCategories(lineItem) {
    return ['EQUIPMENT', 'PART', 'CUSTOM', 'SHIPPING', 'FEE'].includes(
        lineItem.productCategoryId
    );
}

export function isWarrantyCredit(reason, secondaryReason) {
    return (
        reason === ORDER_RETURN_REASON.WARRANTY && secondaryReason === 'CREDIT'
    );
}

export function getReturnReason(
    reasonId,
    secondaryReasonId,
    orderReturnReasons = []
) {
    const reason = orderReturnReasons.find(r => r.id === reasonId);
    if (reason) {
        if (reason.reasons?.length > 0) {
            return reason.reasons.find(r => r.id === secondaryReasonId);
        }
        return reason;
    }
    return null;
}

export function isValidLineItemsForCredit(orderReturn, orderReturnReasons) {
    if (!orderReturn) return false;
    const { returnReason, returnSecondaryReason, lineItems, statusId } =
        orderReturn;
    const returnReasonMeta = getReturnReason(
        returnReason,
        returnSecondaryReason,
        orderReturnReasons
    );
    const validStatuses = [
        ORDER_RETURN_STATUS.APPROVED,
        ORDER_RETURN_STATUS.PARTIAL_APPROVED,
        ORDER_RETURN_STATUS.DENIED,
        ORDER_RETURN_STATUS.COMPLETED,
    ];
    if (
        validStatuses.includes(statusId) &&
        (isMotiliEngineeringDept() || isMotiliFinanceDept())
    ) {
        return lineItems?.every(li => {
            const validCost = returnReasonMeta?.costEditable
                ? isNil(li.vendorCreditAmount) || li.vendorCreditAmount > 0
                : true;
            const validPrice = returnReasonMeta?.priceEditable
                ? isNil(li.clientCreditAmount) || li.clientCreditAmount > 0
                : true;
            return validCost && validPrice;
        });
    }
    return true;
}

export const getReturnRequestApplicableLineItems = (
    returnRequest,
    lineItems
) => {
    const returnRequestLineItems = returnRequest?.lineItems;

    return returnRequestLineItems.filter(reLi => {
        const li = lineItems.find(li => li.id === reLi.id);
        return li && !isPart(li);
    });
};
